import React, { useState, useMemo } from 'react';
import styles from './KPITableWithPills.module.scss';
import { useTranslation } from 'react-i18next';
import { CurrencyInputFormatter } from '../../../../../utils/currencyFormatter';
import { NumberFormatter } from '../../../../../utils/NumberFormatter';
import Icon from '../../../../common/Icon/Icon';
import KPIsPill from '../../../../common/KPIsPill/KPIsPill';

interface KPIDataValue {
  kpiDataType: string;
  kpiValue: string | number;
  kpiCurrency?: string;
  groupName?: string;
}

interface KPITableWithPillsProps {
  data: {
    header: {
      row: KPIDataValue[];
    };
    body: {
      rows: KPIDataValue[][];
    };
  };
  affiliateCode?: string;
  title?: string;
  subtitle?: string;
  applyGrouping?: boolean;
}

const SERIES_COLORS = [
  'var(--data-vis-color-viz-series-01, #5969d6)',
  'var(--data-vis-color-viz-series-02, #f3596c)',
  'var(--data-vis-color-viz-series-03, #396e75)',
  'var(--data-vis-color-viz-series-04, #38539a)',
  'var(--data-vis-color-viz-series-05, #ce4800)',
  'var(--data-vis-color-viz-series-06, #05a896)',
  'var(--data-vis-color-viz-series-07, #7e3e80)',
  'var(--data-vis-color-viz-series-08, #f5a066)',
  'var(--data-vis-color-viz-series-09, #107f47)',
  'var(--data-vis-color-viz-series-10, #b6577e)',
];

const KPITableWithPills: React.FC<KPITableWithPillsProps> = ({
  data,
  affiliateCode,
  title,
  subtitle,
  applyGrouping,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<string>('all');

  // Extract unique group names from the first column of each row
  const groups = useMemo(() => {
    const groupNames = new Set<string>();

    groupNames.add('all');

    data?.body?.rows?.forEach((row) => {
      const firstCell = row[0];

      if (firstCell?.groupName) {
        groupNames.add(firstCell.groupName.toLowerCase());
      }
    });

    return Array.from(groupNames);
  }, [data]);

  const filteredRows = useMemo(() => {
    let rows = applyGrouping
      ? data?.body?.rows?.filter((row) => row[0]?.groupName?.toLowerCase() === selectedGroup) || []
      : data?.body?.rows || [];

    // Filter out rows where all kpiValues (excluding grouping/string data) are 0 or '-'
    rows = rows.filter((row) => {
      const percentage = Number(row[1]?.kpiValue || 0);
      const change = row[2]?.kpiValue === '-' ? 0 : Number(row[2]?.kpiValue || 0);
      const currency = Number(row[3]?.kpiValue || 0);
      const volume = Number(row[4]?.kpiValue || 0);

      // Keep row if any value is non-zero
      return percentage !== 0 || change !== 0 || currency !== 0 || volume !== 0;
    });

    const shouldSummarize = rows.length > 10;
    const topRows = shouldSummarize ? rows.slice(0, 10) : rows;
    const restRows = shouldSummarize ? rows.slice(10) : [];

    if (restRows.length > 0) {
      const sumCurrency = restRows.reduce((acc, row) => acc + Number(row[3]?.kpiValue || 0), 0);
      const sumVolume = restRows.reduce((acc, row) => acc + Number(row[4]?.kpiValue || 0), 0);

      const summaryRow: KPIDataValue[] = [
        { kpiDataType: 'string', kpiValue: 'Rest all Categories', groupName: selectedGroup },
        { kpiDataType: 'number', kpiValue: 0 },
        { kpiDataType: 'string', kpiValue: '-' },
        {
          kpiDataType: 'currency',
          kpiValue: sumCurrency,
          kpiCurrency: restRows[0][3]?.kpiCurrency,
        },
        { kpiDataType: 'number', kpiValue: sumVolume },
      ];
      return [...topRows, summaryRow];
    }

    return topRows;
  }, [data, selectedGroup, applyGrouping]);

  const totalPercentage = useMemo(() => {
    return filteredRows.reduce((acc, row) => acc + Number(row[1]?.kpiValue || 0), 0);
  }, [filteredRows]);

  const getColorForIndex = (index: number) => {
    return SERIES_COLORS[index % SERIES_COLORS.length];
  };

  const renderRow = (row: KPIDataValue[], rowIndex: number) => {
    const nameCell = row[0];
    const percentageCell = row[1];
    const changeCell = row[2];
    const currencyCell = row[3];
    const volumeCell = row[4];

    const categoryName = String(nameCell?.kpiValue || '');
    const isRestCategory = categoryName === 'Rest all Categories';
    const color = getColorForIndex(rowIndex);

    const changeVal = Number(changeCell?.kpiValue || 0);
    const isPositive = changeVal > 0;
    const isNegative = changeVal < 0;

    return (
      <React.Fragment key={rowIndex}>
        {isRestCategory && (
          <tr className={styles.blank_row}>
            <td colSpan={4} />
          </tr>
        )}
        <tr
          className={isRestCategory ? styles.rest_category_row : ''}
          style={{ backgroundColor: 'var(--surface-surface-tertiary, #282828)' }}
        >
          <td className={styles.category_cell}>
            <div className={styles.category_top_row}>
              {!isRestCategory && (
                <div className={styles.color_dot} style={{ backgroundColor: color }} />
              )}
              <span className={styles.category_name}>
                {i18n.exists(`categoryGroups.${categoryName.toLowerCase()}`)
                  ? t(`categoryGroups.${categoryName.toLowerCase()}`)
                  : categoryName}
                {!isRestCategory && `: ${percentageCell?.kpiValue}%`}
              </span>
            </div>
          </td>
          <td className={styles.value_cell}>
            <div
              className={`${styles.change_row} ${isPositive ? styles.positive : ''} ${isNegative ? styles.negative : ''}`}
            >
              {changeCell?.kpiValue !== '-' && (
                <>
                  {isPositive && (
                    <Icon
                      name="up_filled_arrow_table"
                      size={10}
                      color="var(--tag-tag-text-green)"
                    />
                  )}
                  {isNegative && (
                    <Icon
                      name="down_filled_arrow_table"
                      size={10}
                      color="var(--tag-tag-text-red)"
                    />
                  )}
                  <span>{Math.abs(changeVal)}%</span>
                </>
              )}
              {changeCell?.kpiValue === '-' && <span>-</span>}
            </div>
          </td>
          <td className={styles.value_cell}>
            {currencyCell?.kpiDataType === 'currency'
              ? CurrencyInputFormatter(
                  currencyCell.kpiCurrency || 'USD',
                  Number(currencyCell.kpiValue)
                )
              : currencyCell?.kpiValue}
          </td>
          <td className={styles.volume_cell}>
            {volumeCell?.kpiDataType === 'number'
              ? NumberFormatter(affiliateCode || 'US', Number(volumeCell.kpiValue))
              : volumeCell?.kpiValue}{' '}
            BV
          </td>
        </tr>
      </React.Fragment>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {title && i18n.exists(title) ? <h2>{t(title)}</h2> : null}
        {subtitle && i18n.exists(subtitle) ? <p>{t(subtitle)}</p> : null}
      </div>

      {groups.length > 1 && (
        <div className={styles.pills_container}>
          {groups.map((group) => {
            const translated = i18n.exists(`categoryGroups.${group.toLowerCase()}`)
              ? t(`categoryGroups.${group.toLowerCase()}`)
              : group.toLowerCase();

            return (
              <KPIsPill
                key={group}
                code={group.toLowerCase()}
                label={translated}
                isSelected={selectedGroup === group.toLowerCase()}
                onPillClick={(code) => setSelectedGroup(code.toLowerCase())}
              />
            );
          })}
        </div>
      )}

      {/* Stacked Bar Visualization */}
      <div className={styles.stacked_bar_container}>
        {filteredRows.map((row, index) => {
          const name = String(row[0]?.kpiValue || '');

          const percentage = Number(row[1]?.kpiValue || 0);
          if (percentage <= 0) return null;

          const normalizedWidth = totalPercentage > 0 ? (percentage / totalPercentage) * 100 : 0;
          const color = getColorForIndex(index);

          return (
            <div
              key={index}
              className={styles.stacked_bar_segment}
              style={{ width: `${normalizedWidth}%`, backgroundColor: color }}
              title={`${name}: ${percentage}%`}
            />
          );
        })}
      </div>

      <div className={styles.table_container}>
        <table className={styles.table}>
          <tbody>{filteredRows.map((row, rowIndex) => renderRow(row, rowIndex))}</tbody>
        </table>
      </div>
    </div>
  );
};

export default KPITableWithPills;
